import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import useSWR from 'swr'
import {
  flexRender, getCoreRowModel, getFilteredRowModel,
  getSortedRowModel, useReactTable,
} from '@tanstack/react-table'
import { Download, ArrowUpDown } from 'lucide-react'
import { fetcher } from '@/hooks'
import { formatNumber, formatPercent, formatUSD } from '@/lib/format-utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import './data-dashboard.css'

// Shared client formatters: en-US, '-' for missing values.
const number = (value, digits = 2) => formatNumber(value, 0, digits)
const money = value => formatUSD(value)
const percent = value => formatPercent(value, 0, 2)
const points = value => value == null ? '-' : `${number(value * 100)} 点`
const sourceName = value => ({ cell: '本格', pid: 'pid 级', segment: '单元', none: '-',
  package: '本包', country: '本国家', unset: '未设' }[value] ?? value)
const constant = value => value !== null && typeof value === 'object'
  ? Object.entries(value).map(([key, item]) => `${key}=${item}`).join(' ')
  : String(value)
const driftHeader = key => ({ table }) =>
  `k90 · ${table.options.meta?.rules?.[key]?.value.label ?? key}`
const read = (row, path) => path.split('.').reduce((value, key) => value?.[key], row)
const valueTone = (value, kind, rules) => {
  if (value == null) return ''
  if (kind === 'd90') return value >= rules.D90_GRAY_HIGH.value ? 'good' :
    value < rules.D90_GRAY_LOW.value ? 'bad' : 'neutral'
  if (kind === 'roas') return value >= 1 ? 'good' : 'bad'
  if (kind === 'profit') return value >= 0 ? 'good' : 'bad'
  if (kind === 'comparison') return value >= rules.COMPARISON_HIGH.value ? 'good' :
    value <= rules.COMPARISON_LOW.value ? 'bad' : ''
  return ''
}
const numeric = (id, header, format = number, kind) => ({
  id, header, accessorFn: row => read(row, id),
  cell: ({ getValue, table }) => {
    const value = getValue()
    const rules = table.options.meta?.rules
    const tone = valueTone(value, kind, rules)
    return <span className={tone ? `dashboard-${tone}` : ''}>{format(value)}</span>
  },
})
const text = (id, header) => ({ id, header, accessorFn: row => read(row, id) ?? '-' })
const cellColumn = text('name', '格子')
const projectionColumns = ['d30', 'd60', 'd90', 'd180'].map(key =>
  numeric(key, key.toUpperCase(), percent, key === 'd90' ? 'd90' : key === 'd180' ? 'roas' : undefined))
const elasticityColumns = [
  numeric('elasticity.slope', 'D90 斜率 / 翻倍', points),
  numeric('elasticity.slopeWithoutTime', '斜率（无时间项）', points),
  numeric('elasticity.r2', 'R²'), numeric('elasticity.range', '花费区间倍数'),
  numeric('elasticity.weeks', '周数', value => number(value, 0)),
  numeric('elasticity.minWeeklyCost', '最低周花费', money),
  numeric('elasticity.maxWeeklyCost', '最高周花费', money),
  numeric('elasticity.minRange', '区间门槛'),
  { id: 'measured', header: '满足实测门槛', accessorFn: row => row.elasticity.measured ? '是' : '否' },
  numeric('marginalD90', '边际 D90', percent, 'roas'),
]
const recentColumns = ['w1', 'w2'].flatMap(key => [
  numeric(`${key}.dailyCost`, `${key.toUpperCase()} 日花费`, money),
  numeric(`${key}.d3`, `${key.toUpperCase()} D3`, percent),
  numeric(`${key}.d7`, `${key.toUpperCase()} D7`, percent),
  numeric(`${key}.cpi`, `${key.toUpperCase()} CPI`, money),
])
const multiplierColumns = ['k60', 'k90', 'k180'].flatMap(key => [
  numeric(`multipliers.${key}.value`, key),
  { id: `${key}-source`, header: `${key} 来源`, accessorFn: row => sourceName(row.multipliers[key].source) },
  numeric(`multipliers.${key}.cost`, `${key} 样本花费`, money),
  numeric(`multipliers.${key}.cohorts`, `${key} 样本队列数`, value => number(value, 0)),
])
const cellColumns = [
  cellColumn, numeric('pid', 'pid', value => number(value, 0)), text('country', '国家'), text('packageName', '渠道包'),
  numeric('dailyCost', '28 日均花费', money), numeric('cost28', '28 日花费', money),
  numeric('share', '花费占比', percent), numeric('installs28', '28 日安装', value => number(value, 0)),
  numeric('cpi', 'CPI', money), ...projectionColumns, ...multiplierColumns,
  numeric('m', 'm = k180/k90'), numeric('band', '误差带 ±', points), text('bandSource', '误差带来源'),
  numeric('k90DriftA', driftHeader('DRIFT_PERIOD_A')), numeric('k90DriftB', driftHeader('DRIFT_PERIOD_B')),
  text('d30Window.start', 'D30 窗起'), text('d30Window.end', 'D30 窗止'),
  numeric('tail.conservative', 'k_tail 保守'), numeric('tail.optimistic', 'k_tail 乐观'),
  { id: 'tail-source', header: '终值来源', accessorFn: row => sourceName(row.tail.source) },
  numeric('tail.ltvConservative', 'LTV 保守', percent), numeric('tail.ltvOptimistic', 'LTV 乐观', percent),
  ...elasticityColumns, ...recentColumns,
  numeric('historicalMaxDailyCost', '历史最高日花费（7 日）', money),
  text('runStart', '连续投放段起点'), numeric('paidDays', '本段付费天数', value => number(value, 0)),
  numeric('paidAge', '本段日龄', value => number(value, 0)), numeric('maturePaidCost', '本段满 D90 花费', money),
  numeric('profit90', 'D90 段利润 / 日', money, 'profit'),
  numeric('profit90_180', '90–180 段利润 / 日', money, 'profit'),
  numeric('profit180', '≤180 利润 / 日', money, 'profit'),
  numeric('attainableProfit', '可达线参照利润 / 日', money, 'profit'),
  numeric('referenceGap', '较参照差 / 日', money, 'profit'),
  numeric('attainableD180', '可达线 D180', percent),
  numeric('predictionToAttainable', '预测 / 可达线', percent, 'comparison'),
]

function DataTable({ title, rows, columns, rules, children }) {
  const [filter, setFilter] = useState('')
  const [sorting, setSorting] = useState([])
  const table = useReactTable({
    data: rows, columns, state: { globalFilter: filter, sorting },
    onGlobalFilterChange: setFilter, onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(), getFilteredRowModel: getFilteredRowModel(), getSortedRowModel: getSortedRowModel(),
    meta: { rules },
  })
  return <Card>
    <CardHeader className="gap-3 sm:flex-row sm:items-center sm:justify-between">
      <CardTitle className="text-base">{title} <span className="text-sm font-normal text-muted-foreground">{table.getRowModel().rows.length} / {rows.length}</span></CardTitle>
      <Input aria-label={`筛选${title}`} placeholder="筛选格子、pid、国家…" value={filter}
        onChange={event => setFilter(event.target.value)} className="max-w-xs" />
    </CardHeader>
    <CardContent>
      {children}
      <div className="overflow-auto rounded-md border">
        <table className="dashboard-table w-full text-sm" aria-label={title}>
          <thead>{table.getHeaderGroups().map(group => <tr key={group.id}>
            {group.headers.map(header => <th key={header.id}
              aria-sort={header.column.getIsSorted() === 'asc' ? 'ascending' : header.column.getIsSorted() === 'desc' ? 'descending' : 'none'}>
              <button type="button" onClick={header.column.getToggleSortingHandler()} className="inline-flex items-center gap-1">
                {flexRender(header.column.columnDef.header, header.getContext())}
                <ArrowUpDown className="h-3 w-3 text-muted-foreground" aria-hidden="true" />
              </button>
            </th>)}
          </tr>)}</thead>
          <tbody>{table.getRowModel().rows.map(row => <tr key={row.id}>
            {row.getVisibleCells().map(cell => <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>)}
          </tr>)}</tbody>
        </table>
        {!table.getRowModel().rows.length && <p className="p-6 text-center text-muted-foreground">无数据</p>}
      </div>
    </CardContent>
  </Card>
}

function Stat({ label, value, detail }) {
  return <Card><CardContent className="p-4">
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="mt-2 text-2xl font-semibold tabular-nums">{value}</p>
    {detail && <p className="mt-2 text-xs text-muted-foreground">{detail}</p>}
  </CardContent></Card>
}

function Snapshot({ data }) {
  const rules = data.config.rules
  const colored = (value, format, kind) => <span className={`dashboard-${valueTone(value, kind, rules)}`}>{format(value)}</span>
  const monthRows = useMemo(() => data.pids.flatMap(pid => pid.months.map(month => ({
    ...month, pid: pid.pid, name: pid.name,
  }))), [data.pids])
  const targetColumns = useMemo(() => [cellColumn, ...[3, 7, 30, 60, 90, 180].map(day => ({
    id: `d${day}`, header: `D${day} 经营线 / 生效日期`,
    accessorFn: row => row.targets.find(target => target.day === day)?.value,
    cell: ({ row }) => {
      const target = row.original.targets.find(item => item.day === day)
      return target?.value == null ? '未设' : <span>{percent(target.value)}
        <small className="block text-muted-foreground">{target.startDate} · {sourceName(target.source)}</small></span>
    },
  }))], [])
  const pidColumns = useMemo(() => [text('name', '产品'), numeric('dailyCost', '日花费', money),
    ...['d30', 'd60', 'd90', 'd180'].map(key => numeric(`predicted.${key}`, `预测 ${key.toUpperCase()}`, percent, key === 'd90' ? 'd90' : key === 'd180' ? 'roas' : undefined)),
    ...['d30', 'd60', 'd90', 'd180'].map(key => numeric(`attainable.${key}`, `可达线 ${key.toUpperCase()}`, percent)),
    numeric('normal.d180', '常态线 D180', percent),
    { ...numeric('predictionRatio', '预测 / 可达线', percent, 'comparison'),
      accessorFn: row => row.predicted.d180 != null && row.attainable.d180 > 0 ? row.predicted.d180 / row.attainable.d180 : null },
    text('best.month', '历史最好安装月'), numeric('best.dailyProfit180', '历史最好利润 / 日', money, 'profit'),
    numeric('matureMonthCount', '成熟样本月数', value => number(value, 0)),
    { id: 'sampleMonths', header: '可达线取样月', accessorFn: row => row.sampleMonths.join(' / ') || '-' },
    numeric('sampleCost', '取样月合计花费', money),
  ], [])
  const constants = Object.entries({ ...rules, ...Object.fromEntries(Object.entries(data.config.elasticityRanges)
    .map(([country, value]) => [`ELAST_MIN_RANGE · ${country}`, value])) })
    .map(([key, value]) => ({ key, ...value }))
  const download = () => {
    const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }))
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `roas-dashboard-${data.seg}-${data.asOf}.json`
    anchor.click()
    URL.revokeObjectURL(url)
  }
  return <div className="space-y-5">
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-card p-4">
      <div><p className="font-medium">{data.segment.name} · {data.asOf} · {data.cells.length} 格</p>
        <p className="mt-1 text-xs text-muted-foreground">
          {data.snapshot.persisted ? '首次留档' : '临时结果（数据沉淀期内，未留档，每次读取重算）'}
          {' '}{data.snapshot.createdAt} · 版本 {data.revision}
        </p></div>
      <Button variant="outline" onClick={download}><Download />下载同份 JSON</Button>
    </div>
    <div className="flex flex-wrap gap-2 text-xs">
      {Object.entries(data.windows).map(([key, value]) => <span className="rounded-full border bg-muted px-3 py-1.5" key={key}>
        {({ spend: '花费基准', d30: 'D30 队列', d90Mature: 'D90 成熟至', d180Mature: 'D180 成熟至', multiplierStart: '倍数样本起点', w1: 'W1', w2: 'W2' })[key]}
        {' '}{typeof value === 'string' ? value : `${value.start} ～ ${value.end}`}
      </span>)}
    </div>
    <div className="grid grid-cols-2 gap-3 xl:grid-cols-6">
      <Stat label="现花费 / 日" value={money(data.summary.dailyCost)} detail={`含排除项合计 ${money(data.summary.totalDailyCost)}`} />
      <Stat label="组合 D90 / D180" value={<>{colored(data.summary.d90, percent, 'd90')} / {colored(data.summary.d180, percent, 'roas')}</>} />
      <Stat label="现状 ≤180 利润 / 日" value={colored(data.summary.profit180, money, 'profit')} detail={`D90 ${money(data.summary.profit90)} + 90–180 ${money(data.summary.profit90_180)}`} />
      <Stat label="组合历史最好利润 / 日" value={colored(data.summary.historicalBest?.dailyProfit180, money, 'profit')} detail={data.summary.historicalBest?.month ?? '无完整成熟月'} />
      <Stat label="可达线参照利润 / 日" value={colored(data.summary.attainableProfit, money, 'profit')} />
      <Stat label="较参照差 / 日" value={colored(data.summary.referenceGap, money, 'profit')} />
    </div>
    <DataTable title="逐格总表" rows={data.cells} columns={cellColumns} rules={rules} />
    <DataTable title="按 pid 预测与可达线" rows={data.pids} columns={pidColumns} rules={rules}>
      <p className="mb-3 text-xs text-muted-foreground">各 pid 的历史峰值分别列出；组合峰值按同一安装月计算。</p>
    </DataTable>
    <DataTable title="已成熟安装月利润" rows={monthRows} rules={rules} columns={[
      text('name', '产品'), text('month', '安装月'), numeric('cost', '月花费', money),
      numeric('d90', 'D90', percent, 'd90'), numeric('d180', 'D180', percent, 'roas'),
      numeric('profit180', '≤180 月毛利', money, 'profit'), numeric('dailyProfit180', '折合日毛利', money, 'profit'),
    ]} />
    <DataTable title="投放师目标" rows={data.cells} columns={targetColumns} rules={rules} />
    <DataTable title="弹性统计" rows={data.cells} columns={[cellColumn, ...elasticityColumns]} rules={rules} />
    <DataTable title="近两周" rows={data.cells} columns={[cellColumn, ...recentColumns]} rules={rules} />
    <DataTable title="现金视角" rows={data.cash} rules={rules} columns={[
      text('month', '日历月'), numeric('days', '覆盖天数', value => number(value, 0)),
      numeric('cost', '日均 cohort 成本', money), numeric('revenue', '日均 cohort 收入', money),
      numeric('d0_89', 'D0–89 / 日', money), numeric('d90_180', 'D90–180 / 日', money),
      numeric('d181plus', 'D181+ / 日', money), numeric('cashProfit', '现金毛利 / 日', money, 'profit'),
      numeric('profit180', '≤180 毛利 / 日', money, 'profit'), numeric('tailShare', '长尾占比', percent),
    ]}>
      <p className="mb-3 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
        固定覆盖边界：仅含 {data.cashCoverage.installStart} 之后安装的队列，截至 {data.cashCoverage.through}。
      </p>
    </DataTable>
    <DataTable title="数据校验" rows={data.checks} rules={rules} columns={[
      text('name', '校验项'),
      { id: 'status', header: '结果', accessorKey: 'status', cell: ({ getValue }) => <span className={`dashboard-${getValue().toLowerCase()}`}>{getValue()}</span> },
      { id: 'actual', header: '实测值', accessorFn: row => JSON.stringify(row.actual), cell: ({ getValue }) => <details>
        <summary className="cursor-pointer">查看实测值</summary><pre className="max-w-2xl whitespace-pre-wrap break-all text-xs">{getValue()}</pre>
      </details> },
    ]} />
    <DataTable title="被排除格子" rows={data.excluded} rules={rules} columns={[
      cellColumn, numeric('dailyCost', '28 日均花费', money), numeric('cost28', '28 日花费', money),
      numeric('cost90', '90 日花费', money), numeric('cohortRevenue90', '90 日 cohort 收入', money),
      { id: 'reason', header: '原因', accessorFn: row => ({ below_min_daily_cost: '低于日均花费门槛',
        insufficient_multiplier_samples: '倍数或 D30 样本不足', missing_cohort_data: '缺失队列回传' })[row.reason] },
    ]} />
    <Card><CardHeader><CardTitle className="text-base">口径与公式</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <dl className="grid gap-3 text-sm">{data.formulas.map(formula => <div key={formula.name}>
          <dt className="font-medium">{formula.name}</dt><dd className="mt-1 text-muted-foreground">{formula.expression}</dd>
        </div>)}</dl>
        <div className="overflow-auto"><table className="dashboard-table w-full text-sm"><thead><tr><th>常量</th><th>当前值</th><th>谁定</th><th>日期</th></tr></thead>
          <tbody>{constants.map(item => <tr key={item.key}><td>{item.key}</td><td>{constant(item.value)}</td><td>{item.decidedBy}</td><td>{item.decidedAt}</td></tr>)}</tbody>
        </table></div>
        {data.config.changeLog.map(entry => <p className="text-xs text-muted-foreground" key={entry.revision}>{entry.date} · {entry.author} · {entry.revision} · {entry.changes}</p>)}
      </CardContent>
    </Card>
  </div>
}

export default function DataDashboard() {
  const [params, setParams] = useSearchParams()
  const [segment, setSegment] = useState(params.get('seg') ?? '')
  const [date, setDate] = useState(params.get('as_of') ?? '')
  const metadata = useSWR(['/roas-dashboard'], fetcher, { revalidateOnFocus: false })
  const active = params.get('seg')
  const activeDate = params.get('as_of') ?? ''
  const query = activeDate ? { as_of: activeDate } : {}
  const result = useSWR(active ? [`/roas-dashboard/${encodeURIComponent(active)}`, query] : null, fetcher,
    { revalidateOnFocus: false, keepPreviousData: false })
  const data = result.data?.data
  const error = metadata.error || result.error
  useEffect(() => {
    setSegment(active ?? '')
    setDate(activeDate)
  }, [active, activeDate])
  return <div className="roas-data-dashboard space-y-5 p-2 md:p-4">
    <header className="space-y-2"><h1 className="text-2xl font-semibold">投放数据盘</h1>
      <p className="text-sm text-muted-foreground">ROAS Data Dashboard · 同一单元、同一截止日、同一份数据</p></header>
    <form className="flex flex-wrap items-end gap-4 rounded-lg border bg-card p-4" onSubmit={event => {
      event.preventDefault()
      if (segment === active && date === activeDate) result.mutate()
      else setParams(date ? { seg: segment, as_of: date } : { seg: segment })
    }}>
      <label className="space-y-2 text-sm"><span className="block">单元</span>
        <select aria-label="单元" required value={segment} onChange={event => setSegment(event.target.value)}
          className="h-9 w-full rounded-md border bg-background px-3 sm:min-w-64">
          <option value="">选择单元</option>
          {metadata.data?.data?.segments.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
        </select>
      </label>
      <label className="space-y-2 text-sm"><span className="block">数据截止日（留空自动探测）</span>
        <Input aria-label="数据截止日" type="date" value={date} min="2025-05-01" onChange={event => setDate(event.target.value)} />
      </label>
      <Button type="submit" disabled={!segment || result.isLoading}>读取数据</Button>
    </form>
    {error && <div role="alert" className="rounded-md border border-destructive p-4 text-destructive">读取失败：{error.message}</div>}
    {result.isLoading && <p role="status" className="p-6 text-muted-foreground">正在读取并留档数据…</p>}
    {!active && !error && <p className="p-6 text-muted-foreground">选择单元后读取数据。截止日留空时，自动探测有成本且队列已回传的最新一天。</p>}
    {!metadata.isLoading && metadata.data?.data?.segments.length === 0 && <p className="p-6 text-muted-foreground">当前账号没有完整单元的 ROAS 数据权限。</p>}
    {data && !result.isLoading && !result.error && <Snapshot key={`${data.seg}:${data.asOf}`} data={data} />}
  </div>
}
